import { useCallback, useMemo, useState } from "react";
import { Button, Modal } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { semantic } from "../../../theme/tokens";

/*
 * Phát hiện dòng phải thu 0đ ở màn thêm mới / chỉnh sửa đợt thu.
 *
 * Tổng một dòng là `max(0, tổng phí − giảm trừ)`, nên giảm trừ ≥ tổng phí làm
 * dòng lặng lẽ thành 0đ mà người nhập không hề hay — đây là nguồn sinh khoản
 * thu 0đ trong dữ liệu. 0đ vẫn có thể hợp lệ (hộ được miễn 100%), nên chỉ
 * CẢNH BÁO trước khi lưu, không chặn.
 */

/** Tổng phải thu của một dòng — cùng công thức với cột "Tổng cộng". */
export const entryTotal = (eData = {}, fees = []) => {
  const subtotal = fees.reduce((sum, fee) => sum + (Number(eData[fee.id]) || 0), 0);
  return Math.max(0, subtotal - (Number(eData.discount_override) || 0));
};

/** Dòng còn hiệu lực có tổng 0đ. Dòng đã hủy không còn là nghĩa vụ thu nên bỏ qua. */
export const findZeroEntries = (entries, entryValues, fees) =>
  entries.filter(
    (entry) => entry.status !== "CANCELLED" && entryTotal(entryValues[entry.id], fees) === 0,
  );

/**
 * Lọc dòng 0đ cho bảng nhập liệu.
 *
 * Lọc theo DANH SÁCH CHỤP LÚC BẬT chứ không theo giá trị sống: lọc sống thì
 * vừa sửa một ô cho hết 0đ là dòng biến mất ngay giữa lúc đang nhập.
 */
export function useZeroFeeFilter(entries, entryValues, fees) {
  const zeroEntries = useMemo(
    () => findZeroEntries(entries, entryValues, fees),
    [entries, entryValues, fees],
  );
  const [zeroFilterIds, setZeroFilterIds] = useState(null);

  const showZero = useCallback(
    (list = zeroEntries) => setZeroFilterIds(new Set(list.map((entry) => entry.id))),
    [zeroEntries],
  );
  const clearZero = useCallback(() => setZeroFilterIds(null), []);
  const toggleZero = useCallback(
    () => (zeroFilterIds ? clearZero() : showZero()),
    [zeroFilterIds, clearZero, showZero],
  );

  return { zeroEntries, zeroFilterIds, showZero, clearZero, toggleZero };
}

/**
 * Hỏi trước khi lưu khi còn dòng 0đ.
 * @returns {Promise<boolean>} true = vẫn lưu, false = quay lại xem các dòng đó
 */
export const confirmZeroEntries = (count) =>
  new Promise((resolve) => {
    Modal.confirm({
      title: `Có ${count} khách hàng phải thu 0đ`,
      icon: <ExclamationCircleOutlined style={{ color: semantic.warning }} />,
      content:
        "Thường do giảm trừ bằng hoặc lớn hơn tổng phí. Nếu đúng là miễn thu thì vẫn lưu được; nếu nhập nhầm, hãy xem lại các dòng này trước.",
      okText: "Vẫn lưu",
      cancelText: "Xem các dòng này",
      centered: true,
      maskClosable: false,
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });

/** Nút lọc trên thanh lọc. Ẩn khi không có dòng 0đ, trừ lúc đang bật để còn chỗ tắt. */
export const ZeroFeeFilterButton = ({ count, active, onToggle }) => {
  if (count === 0 && !active) return null;
  return (
    <Button
      danger
      type={active ? "primary" : "default"}
      icon={<ExclamationCircleOutlined />}
      onClick={onToggle}
      style={{ width: "100%" }}
    >
      Khoản 0đ ({count})
    </Button>
  );
};
